import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const adminService = {
  async checkAdmin() {
    const res = await axios.get(`${API}/admin/me/check`);
    return res.data;
  },

  async listUsers({ search = '', limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (search) params.append('search', search);
    const res = await axios.get(`${API}/admin/users?${params}`);
    return res.data;
  },

  async getUser(userId) {
    const res = await axios.get(`${API}/admin/users/${userId}`);
    return res.data;
  },

  async adjustCredits(userId, delta, note = '') {
    const res = await axios.patch(`${API}/admin/users/${userId}/credits`, { delta, note });
    return res.data;
  },

  async setUserStatus(userId, enabled) {
    const res = await axios.patch(`${API}/admin/users/${userId}/status`, { enabled });
    return res.data;
  },

  async getPlatformBackground() {
    const res = await axios.get(`${API}/platform/background`);
    return res.data;
  },

  async setPlatformBackground(url) {
    const res = await axios.put(`${API}/admin/platform/background`, { url });
    return res.data;
  },

  async uploadPlatformBackground(file) {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post(`${API}/admin/platform/background/upload`, form);
    return res.data;
  },

  // ── E6 · LMX (Lumière nuevo) ──
  async getLmxConfig() {
    const res = await axios.get(`${API}/admin/lmx/config`);
    return res.data;
  },

  async putLmxConfig(patch) {
    const res = await axios.put(`${API}/admin/lmx/config`, patch);
    return res.data;
  },

  async getLmxUsage() {
    const res = await axios.get(`${API}/admin/lmx/usage`);
    return res.data;
  },

  async uploadLmxAsset(file) {
    const form = new FormData();
    form.append('file', file);
    form.append('origin', 'lmx-admin');
    const res = await axios.post(`${API}/files/upload`, form);
    return res.data;
  },

  async listExecutions(userId, { limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    const res = await axios.get(`${API}/admin/users/${userId}/executions?${params}`);
    return res.data;
  },

  async listPayments(userId, { limit = 50 } = {}) {
    const res = await axios.get(`${API}/admin/users/${userId}/payments?limit=${limit}`);
    return res.data;
  },

  async listAssets(userId, { assetType = null, limit = 60 } = {}) {
    const params = new URLSearchParams({ limit });
    if (assetType) params.append('asset_type', assetType);
    const res = await axios.get(`${API}/admin/users/${userId}/assets?${params}`);
    return res.data;
  },

  async bulkAdjustCredits({ userIds, delta, note = '' }) {
    const res = await axios.post(`${API}/admin/users/bulk/credits`, {
      user_ids: userIds, delta, note,
    });
    return res.data;
  },

  async getMetrics() {
    const res = await axios.get(`${API}/admin/metrics`);
    return res.data;
  },

  // ── Feature A: node health (passive, read-only) ──
  async getNodesHealth({ window = null } = {}) {
    const qs = window ? `?window=${window}` : '';
    const res = await axios.get(`${API}/admin/health/nodes${qs}`);
    return res.data;
  },

  // ── Feature B: security events + consumption spikes ──
  async listSecurityEvents({ userId = '', eventType = '', since = '', until = '', limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (userId) params.append('user_id', userId);
    if (eventType) params.append('event_type', eventType);
    if (since) params.append('since', since);
    if (until) params.append('until', until);
    const res = await axios.get(`${API}/admin/security/events?${params}`);
    return res.data;
  },

  async getConsumptionSpikes() {
    const res = await axios.get(`${API}/admin/security/spikes`);
    return res.data;
  },

  async getSecurityConfig() {
    const res = await axios.get(`${API}/admin/security/config`);
    return res.data;
  },

  async saveSecurityConfig(patch) {
    const res = await axios.put(`${API}/admin/security/config`, patch);
    return res.data;
  },

  // ── Control de acceso MCP (doble capa: global + por usuario) ──
  async getMcpAccess({ search = '', limit = 50 } = {}) {
    const params = new URLSearchParams({ limit });
    if (search) params.append('search', search);
    const res = await axios.get(`${API}/admin/mcp/access?${params}`);
    return res.data;
  },

  async setMcpGlobal(enabled) {
    const res = await axios.put(`${API}/admin/mcp/access`, { global_enabled: enabled });
    return res.data;
  },

  async setMcpUserLimits(userId, limits) {
    const res = await axios.put(`${API}/admin/mcp/access/users/${userId}/limits`, limits);
    return res.data;
  },

  async listMcpLimitRequests(status = 'pending') {
    const res = await axios.get(`${API}/admin/mcp/limit-requests?status=${status}`);
    return res.data;
  },

  async decideMcpLimitRequest(requestId, { approve, approvedValue = null, note = '' }) {
    const res = await axios.post(`${API}/admin/mcp/limit-requests/${requestId}/decide`, {
      approve, approved_value: approvedValue, admin_note: note,
    });
    return res.data;
  },

  async setMcpUser(userId, enabled) {
    const res = await axios.put(`${API}/admin/mcp/access/users/${userId}`, { enabled });
    return res.data;
  },

  // ── Envío 3 · Paso 7: Costos y margen (USD) ──
  async getCostsSummary({ from, to } = {}) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const res = await axios.get(`${API}/admin/costs/summary?${params}`);
    return res.data;
  },

  async getCostsBy({ dim = 'model', from, to } = {}) {
    const params = new URLSearchParams({ dim });
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const res = await axios.get(`${API}/admin/costs/by?${params}`);
    return res.data;
  },

  async listCostExecutions({
    from, to, modelId, resultType, userId, costSource, execStatus,
    onlyNegative = false, limit = 50, offset = 0,
  } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (modelId) params.append('model_id', modelId);
    if (resultType) params.append('result_type', resultType);
    if (userId) params.append('user_id', userId);
    if (costSource) params.append('cost_source', costSource);
    if (execStatus) params.append('exec_status', execStatus);
    if (onlyNegative) params.append('only_negative', 'true');
    const res = await axios.get(`${API}/admin/costs/executions?${params}`);
    return res.data;
  },

  async getCostDetail(executionId) {
    const res = await axios.get(`${API}/admin/costs/executions/${executionId}`);
    return res.data;
  },

  async downloadCostsCsv({
    from, to, modelId, resultType, userId, costSource, execStatus,
    onlyNegative = false,
  } = {}) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (modelId) params.append('model_id', modelId);
    if (resultType) params.append('result_type', resultType);
    if (userId) params.append('user_id', userId);
    if (costSource) params.append('cost_source', costSource);
    if (execStatus) params.append('exec_status', execStatus);
    if (onlyNegative) params.append('only_negative', 'true');
    const res = await axios.get(`${API}/admin/costs/export.csv?${params}`, {
      responseType: 'blob',
    });
    return res.data;
  },

  async listPricing({ includeHistory = false } = {}) {
    const res = await axios.get(
      `${API}/admin/costs/pricing?include_history=${includeHistory}`);
    return res.data;
  },

  async savePricing(body) {
    const res = await axios.post(`${API}/admin/costs/pricing`, body);
    return res.data;
  },

  async closePricing(pricingId) {
    const res = await axios.delete(`${API}/admin/costs/pricing/${pricingId}`);
    return res.data;
  },

  // ── Envío 4: precio dinámico ──
  async getPricingConfig() {
    const res = await axios.get(`${API}/admin/costs/pricing-config`);
    return res.data;
  },

  async savePricingConfig(patch) {
    const res = await axios.put(`${API}/admin/costs/pricing-config`, patch);
    return res.data;
  },

  async previewPricingConfig(config) {
    const res = await axios.post(`${API}/admin/costs/pricing-config/preview`, { config });
    return res.data;
  },

  // ── Envío 5: Motor de IA ──
  async getLlmConfig() {
    const res = await axios.get(`${API}/admin/llm/config`);
    return res.data;
  },

  async saveLlmConfig(config) {
    const res = await axios.put(`${API}/admin/llm/config`, config);
    return res.data;
  },

  async listLlmModels(provider, toolsOnly = false) {
    const res = await axios.get(
      `${API}/admin/llm/models?provider=${provider}&tools_only=${toolsOnly}`);
    return res.data;
  },

  async testLlmEngine(body) {
    const res = await axios.post(`${API}/admin/llm/test`, body);
    return res.data;
  },

  async getLlmUsage({ group = 'feature', from, to } = {}) {
    const params = new URLSearchParams({ group });
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const res = await axios.get(`${API}/admin/llm/usage?${params}`);
    return res.data;
  },
};
